/**
 * Chat (Workforce.ai) usage indicator. Mirrors the People credit-usage-indicator
 * capabilities with three display modes:
 * - button: a button + popover summary (compact, for nav/header use)
 * - inline: a compact inline box with progress bars
 * - detailed: a fuller, collapsible card (default, for the billing page)
 * - sidebar: a bordered icon button showing the plan name + a usage gauge
 *   (follows the left-nav plan-badge pattern), opening the same usage popover.
 *
 * Chat has two metered dimensions (snapshot + deep research queries), so each
 * mode renders one bar per dimension.
 */

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import * as dayjs from 'dayjs';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ChatCreditUsage, ChatCreditsService } from 'src/app/core/services/chat-credits.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { HostnameService } from 'src/app/hostname-router-service';
import { CreditTypeBalance, foldCreditBySource } from 'src/app/shared/utils/credit-sources';

interface ChatUsageMetric {
  label: string;
  used: number;
  limit: number | 'unlimited' | null;
  // Credit split by type: remaining one-time/purchased/etc. balances from the
  // balance endpoint plus the settings-configured monthly allowance; null
  // until the breakdown has loaded.
  breakdown?: CreditTypeBalance[] | null;
}

@Component({
  selector: 'app-chat-usage-indicator',
  templateUrl: './chat-usage-indicator.component.html',
  styleUrls: ['./chat-usage-indicator.component.scss'],
  standalone: false,
})
export class ChatUsageIndicatorComponent implements OnInit, OnDestroy {
  // 'caption' renders projected content (e.g. the composer's queries-remaining
  // text) as the popover trigger.
  @Input() displayMode: 'button' | 'inline' | 'detailed' | 'sidebar' | 'caption' = 'detailed';
  @Input() hideText: boolean = false;
  @Input() hideProgressBar: boolean = false;
  @Input() transparentBackground: boolean = false;
  // Optional explicit plan name; when omitted the plan is derived from the quotas.
  @Input() planName?: string;

  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  usage: ChatCreditUsage | null = null;
  isDetailCollapsed: boolean = false;
  // Credit split by type per query dimension: the settings-configured monthly
  // allowance (in hand immediately) plus the one-time balances from the
  // balance endpoint, built when the popover opens.
  snapshotBreakdown: CreditTypeBalance[] | null = null;
  deepResearchBreakdown: CreditTypeBalance[] | null = null;
  // The one-time balances are still being fetched / failed to fetch; the
  // monthly rows stay visible either way.
  breakdownLoading: boolean = false;
  breakdownError: boolean = false;
  private usageSub: Subscription;

  get metrics(): ChatUsageMetric[] {
    if (!this.usage) {
      return [];
    }
    return [
      {
        label: 'Snapshot queries',
        used: this.usage.snapshotUsed,
        limit: this.usage.snapshotLimit,
        breakdown: this.snapshotBreakdown,
      },
      {
        label: 'Deep research queries',
        used: this.usage.deepResearchUsed,
        limit: this.usage.deepResearchLimit,
        breakdown: this.deepResearchBreakdown,
      },
    ];
  }

  // The "resets in N days" line only applies to orgs that actually have a
  // recurring monthly allowance configured in their org settings (which keeps
  // it showing when the allowance is fully spent mid-cycle).
  get showResetLine(): boolean {
    return (
      (this.usage?.snapshotMonthlyCredit || 0) + (this.usage?.deepResearchMonthlyCredit || 0) > 0
    );
  }

  // Worst-case percentage across the metered dimensions, for the button mini bar.
  get overallPercent(): number {
    const pcts = this.metrics
      .filter((m) => !this.isUnlimited(m.limit))
      .map((m) => this.percent(m.used, m.limit));
    return pcts.length ? Math.max(...pcts) : 0;
  }

  // At least one dimension has a real (numeric) quota to chart against.
  get hasMeteredQuota(): boolean {
    return this.metrics.some((m) => !this.isUnlimited(m.limit));
  }

  // Fewest queries left across the metered dimensions (null when all unlimited).
  // Drives a low-balance warning for small plans where 70% never trips (e.g.
  // Free's 3 deep-research queries).
  get lowestRemaining(): number | null {
    const remaining = this.metrics
      .filter((m) => !this.isUnlimited(m.limit))
      .map((m) => Math.max(0, (m.limit as number) - m.used));
    return remaining.length ? Math.min(...remaining) : null;
  }

  // Gauge fill: worst-case usage when metered, full ring when everything's unlimited.
  get gaugePercent(): number {
    return this.hasMeteredQuota ? this.overallPercent : 100;
  }

  // Plan name for the sidebar badge: explicit input, else the service-provided
  // plan (resolved authoritatively from the org's active subscriptions), else
  // Free. We never infer the plan from quota limits — an org with no chat
  // subscription is Free regardless of any custom limits in its settings.
  get planLabel(): string {
    return this.planName ?? this.usage?.planName ?? 'Free';
  }

  // Usage card lists deep research first, then snapshot, to match the design.
  get cardMetrics(): ChatUsageMetric[] {
    return [...this.metrics].reverse();
  }

  // Whole days until the usage cycle resets (>= 0); 0 means it resets today.
  get resetInDays(): number {
    if (!this.usage?.resetDate) {
      return 0;
    }
    return Math.max(0, dayjs(this.usage.resetDate).startOf('day').diff(dayjs().startOf('day'), 'day'));
  }

  // Sales CTA for the usage card; null when already on a top plan. Plan
  // changes go through the team for now, so the CTA opens Intercom.
  get salesCtaLabel(): string | null {
    switch (this.planLabel?.toLowerCase()) {
      case 'free':
      case 'pro':
        return 'Contact Sales';
      default:
        return null;
    }
  }

  constructor(
    private chatCreditsService: ChatCreditsService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.usageSub = this.chatCreditsService
      .getChatCreditUsage()
      .subscribe((usage) => (this.usage = usage));
  }

  // Build the per-query-type breakdowns: the monthly allowances come from org
  // settings and are already in hand, so show them immediately; only the
  // remaining one-time and purchased balances need fetching from the balance
  // endpoint. Called whenever the popover opens so it reflects the current cycle.
  loadBreakdown(): void {
    this.snapshotBreakdown = foldCreditBySource([], this.usage?.snapshotMonthlyCredit || 0);
    this.deepResearchBreakdown = foldCreditBySource([], this.usage?.deepResearchMonthlyCredit || 0);
    this.breakdownLoading = true;
    this.breakdownError = false;
    this.chatCreditsService.getChatCreditBreakdown().subscribe({
      next: (breakdown) => {
        this.breakdownLoading = false;
        if (!breakdown) {
          // Keep the settings-sourced monthly rows; only the one-time fetch failed.
          this.breakdownError = true;
          return;
        }
        this.snapshotBreakdown = foldCreditBySource(
          breakdown.snapshot,
          this.usage?.snapshotMonthlyCredit || 0
        );
        this.deepResearchBreakdown = foldCreditBySource(
          breakdown.deepResearch,
          this.usage?.deepResearchMonthlyCredit || 0
        );
      },
      error: () => {
        this.breakdownLoading = false;
        this.breakdownError = true;
      },
    });
  }

  isUnlimited(limit: number | 'unlimited' | null): boolean {
    return limit === 'unlimited' || limit === null;
  }

  percent(used: number, limit: number | 'unlimited' | null): number {
    if (typeof limit !== 'number' || limit < 0) {
      return 0;
    }
    if (limit === 0) {
      // A real zero quota (credit-mode org with no credits): there is no
      // capacity at all, so the meter reads full even at 0 usage, matching
      // CreditUsageService's zero-quota handling.
      return 100;
    }
    return Math.min(100, Math.round((used / limit) * 100));
  }

  // Queries left for a single metric (null when unlimited), for per-bar warnings.
  metricRemaining(metric: ChatUsageMetric): number | null {
    return this.isUnlimited(metric.limit)
      ? null
      : Math.max(0, (metric.limit as number) - metric.used);
  }

  barColor(pct: number, remaining: number | null = null): string {
    switch (ChatCreditsService.usageSeverity(pct, remaining)) {
      case 'danger':
        return 'tw-bg-red-500';
      case 'warn':
        return 'tw-bg-yellow-500';
      default:
        return 'tw-bg-emerald-500';
    }
  }

  // Usage-card meter color: teal when healthy, amber as it fills / runs low, red near the cap.
  meterColor(pct: number, remaining: number | null = null): string {
    switch (ChatCreditsService.usageSeverity(pct, remaining)) {
      case 'danger':
        return 'tw-bg-red-500';
      case 'warn':
        return 'tw-bg-amber-400';
      default:
        return 'tw-bg-teal-400';
    }
  }

  // SVG stroke for the sidebar gauge (teal = unlimited, else threshold colors).
  gaugeStroke(): string {
    if (!this.hasMeteredQuota) {
      return '#14b8a6'; // teal-500
    }
    switch (ChatCreditsService.usageSeverity(this.overallPercent, this.lowestRemaining)) {
      case 'danger':
        return '#ef4444'; // red-500
      case 'warn':
        return '#eab308'; // yellow-500 (70%+ used, or <= 1 query left)
      default:
        return '#10b981'; // emerald-500
    }
  }

  toggleDetailCollapse(): void {
    this.isDetailCollapsed = !this.isDetailCollapsed;
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.menuTrigger?.menuOpen) {
      this.closeMenu();
    }
  }

  closeMenu(): void {
    if (this.menuTrigger && this.menuTrigger.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  goToBilling(): void {
    this.closeMenu();
    if (HostnameService.isWorkforceExperience()) {
      this.router.navigate(['/billing']);
    } else {
      this.router.navigate([`/${this.authService.getSelectedOrgIdValue}/settings`]);
    }
  }

  // Plan changes go through the team for now.
  contactSales(): void {
    this.closeMenu();
    (window as any)['Intercom']('showNewMessage');
  }

  ngOnDestroy(): void {
    this.usageSub?.unsubscribe();
  }
}
